/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import type { Capability } from '../../constants/capabilities';
import type { ViewPlugin } from '../../types/ViewPlugin';

export function getDependantPlugins( {
	capabilities,
	plugin
}: {
	capabilities: ReadonlyArray<Capability>;
	plugin: ViewPlugin;
} ): Array<string> {
	const validDependantPlugins = capabilities
		.flatMap( capability => capability.features )
		.flatMap( feature => feature.plugins );

	function isValid( requirement: string | Array<string> ): boolean {
		if ( typeof requirement !== 'string' ) {
			return true;
		}

		return validDependantPlugins.includes( requirement );
	}

	return plugin.requires
		.filter( isValid )
		.filter( ( requirement, i, allRequirements ) => {
			if ( !Array.isArray( requirement ) ) {
				return true;
			}

			// If the requirement is an alternating one, but one of alternating
			// items is included as a regular requirement, then the array is redundant.
			if ( requirement.some( r => allRequirements.includes( r ) ) ) {
				return false;
			}

			// If the feature requires itself as an alternating requirement, then that requirement is always fulfilled.
			// This can happen when plugin A requiring plugin B both end up in the same feature.
			if ( requirement.includes( plugin.className ) ) {
				return false;
			}

			return true;
		} )
		.map( requiredPlugin => {
			if ( typeof requiredPlugin === 'string' ) {
				return requiredPlugin;
			}

			return requiredPlugin.filter( isValid ).join( ' or ' );
		} );
}
